/**
 * PipelineGroup — one cost-center section of the pipeline board.
 *
 * A sticky header (title + pipeline count + an overridden badge when there is
 * anything to say) over a MUI Collapse of the group's rows. The header is the
 * whole toggle target, matching the Sidebar's navigation groups.
 *
 * The overridden badge deliberately reads "N overridden", never the bare word:
 * "overridden" alone is the per-row state chip, and a group badge must not
 * shadow it.
 */

import React from 'react';
import { Box, Typography, Chip, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { colors, typography, zIndex } from '../../../theme/designTokens';

export interface PipelineGroupProps {
  title: string;
  /** Rows currently shown in this group (post-filter). */
  count: number;
  /** How many of those rows are overridden away from their default. */
  overriddenCount: number;
  expanded: boolean;
  onToggle: () => void;
  /** Distance from the top of the scroll container; sits under the filter bar. */
  stickyTop: number;
  children: React.ReactNode;
}

export const PipelineGroup: React.FC<PipelineGroupProps> = ({
  title,
  count,
  overriddenCount,
  expanded,
  onToggle,
  stickyTop,
  children,
}) => (
  <Box sx={{ mb: 2 }}>
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        position: 'sticky',
        top: stickyTop,
        zIndex: zIndex.sticky - 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        mb: 1,
        cursor: 'pointer',
        userSelect: 'none',
        borderRadius: '10px',
        backgroundColor: colors.gray[50],
        borderBottom: `1px solid ${colors.gray[200]}`,
        '&:focus-visible': { outline: `2px solid ${colors.deepTeal[400]}`, outlineOffset: -2 },
      }}
    >
      {expanded ? (
        <ExpandLessIcon sx={{ fontSize: 18, color: colors.gray[600] }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray[600] }} />
      )}
      <Typography
        variant="overline"
        sx={{
          fontFamily: typography.fonts.heading,
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          color: colors.gray[700],
          lineHeight: 1,
        }}
      >
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {count === 1 ? '1 pipeline' : `${count} pipelines`}
      </Typography>
      {overriddenCount > 0 && (
        <Chip
          label={`${overriddenCount} overridden`}
          size="small"
          sx={{
            height: 20,
            fontWeight: 600,
            color: colors.warning.dark,
            backgroundColor: colors.warning[50],
            border: `1px solid ${colors.warning.light}`,
          }}
        />
      )}
    </Box>
    <Collapse in={expanded} timeout="auto" unmountOnExit>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{children}</Box>
    </Collapse>
  </Box>
);

export default PipelineGroup;
